/**
 * Pictogrammes des visuels (31/10/2026) : un vocabulaire fermé, en français, que l'agent visuel peut choisir pour
 * chaque élément (étape, niveau, jalon, branche, case). Le serveur les dessine à partir de la bibliothèque Lucide
 * (licence ISC). Sans choix de l'agent, un pictogramme est déduit des mots du libellé ; sinon, le numéro.
 * Module pur.
 */
export const PICTOS = {
  idee: "Lightbulb",
  cible: "Target",
  equipe: "Users",
  personne: "User",
  document: "FileText",
  valider: "CircleCheck",
  alerte: "TriangleAlert",
  outil: "Wrench",
  reglage: "Settings",
  graphique: "ChartColumn",
  calendrier: "Calendar",
  temps: "Clock",
  echange: "MessagesSquare",
  ecoute: "Ear",
  recherche: "Search",
  livre: "BookOpen",
  diplome: "GraduationCap",
  securite: "ShieldCheck",
  argent: "Euro",
  progres: "TrendingUp",
  boucle: "RefreshCw",
  objectif: "Flag",
  lancement: "Rocket",
  bienveillance: "Heart",
  reflexion: "Brain",
  geste: "Hand",
  droit: "Scale",
  entreprise: "Building",
  numerique: "Laptop",
  telephone: "Phone",
  courriel: "Mail",
  parcours: "Map",
  qualite: "Star",
  cle: "KeyRound",
  methode: "Puzzle",
  reussite: "Trophy",
  environnement: "Leaf",
  ferroviaire: "TrainFront",
  logistique: "Truck",
  sante: "HeartPulse",
  liste: "ListChecks",
  question: "CircleQuestionMark",
  chantier: "HardHat",
  energie: "Zap",
  accord: "Handshake",
  observation: "Eye",
  communication: "Megaphone",
  ecriture: "PenLine",
  mesure: "Gauge",
  niveaux: "Layers",
  orientation: "Compass",
} as const;
export type Picto = keyof typeof PICTOS;
export const IDS_PICTOS = Object.keys(PICTOS) as Picto[];
export const estPicto = (x: unknown): x is Picto => typeof x === "string" && x in PICTOS;

/** Mots (radicaux, sans accents) → pictogramme, pour déduire un picto d'un libellé. */
const INDICES: Array<[RegExp, Picto]> = [
  [/securi|risque|protec|epi\b/, "securite"],
  [/danger|erreur|vigilan|piege|alerte/, "alerte"],
  [/accueil|ecout/, "ecoute"],
  [/questio|quiz/, "question"],
  [/echang|dialog|entretien|discussion|feedback|retour/, "echange"],
  [/equipe|collectif|groupe|manag/, "equipe"],
  [/client|stagiaire|apprenant|salarie|personne|candidat/, "personne"],
  [/objectif|cible|but\b/, "cible"],
  [/plan|prepar|organis|calendrier|planning/, "calendrier"],
  [/temps|duree|delai|minute|heure/, "temps"],
  [/analys|diagnost|recherch|identifi|reperer/, "recherche"],
  [/mesur|indicateur|kpi|evalu/, "mesure"],
  [/progres|croiss|amelior|evolu/, "progres"],
  [/cycle|boucle|iter|recommenc/, "boucle"],
  [/valid|controle|verifi|conform/, "valider"],
  [/document|contrat|dossier|ecrit|rediger|convention/, "document"],
  [/loi|regle|juridi|droit|norme|reglement/, "droit"],
  [/outil|materiel|equipement/, "outil"],
  [/process|methode|procedur|etape/, "methode"],
  [/idee|creativ|innov|solution/, "idee"],
  [/reussi|succes|resultat|performan/, "reussite"],
  [/argent|budget|cout|prix|financ|euro/, "argent"],
  [/numeriq|logiciel|digital|informati|ordinateur/, "numerique"],
  [/communi|presenter|annonc/, "communication"],
  [/accord|negoci|partenar|contractualis/, "accord"],
  [/forma|apprend|cours|savoir|connaiss/, "livre"],
  [/certif|diplom|qualif/, "diplome"],
  [/sante|soin|bien-etre|stress/, "sante"],
  [/environ|ecolog|durable|dechet/, "environnement"],
  [/train|ferroviaire|rail|voie/, "ferroviaire"],
  [/observ|regard|voir\b/, "observation"],
  [/lanc|demarr|debut|commenc/, "lancement"],
  [/decid|choix|choisir|orient/, "orientation"],
  [/qualit|excellen/, "qualite"],
  [/reflech|comprend|memoris|cognit/, "reflexion"],
];

const sansAccents = (t: string) => t.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Pictogramme d'un élément : celui choisi par l'agent s'il est connu, sinon déduit du texte, sinon `null`. */
export function pictoPour(choisi: unknown, ...textes: string[]): Picto | null {
  // Les sorties structurées peuvent renvoyer une valeur d'énumération avec une autre casse (documentation Anthropic).
  const c = typeof choisi === "string" ? sansAccents(choisi.trim()) : choisi;
  if (estPicto(c)) return c;
  const t = sansAccents(textes.join(" "));
  return INDICES.find(([re]) => re.test(t))?.[1] ?? null;
}
